/**
 * DepthText v1.2.0 - TypeScript
 * https://mobiwise.dev | https://github.com/mobiwise-dev/depthtext
 * MIT License | (c) 2025
 *
 * - Compatible SSR (guards around window/document)
 * - Designed to be bundled (tsup / rollup / vite)
 * - Exports: DepthTextInstance, DEPTHTEXT_DEFAULTS, DepthTextify
 */

/* ===========================================================================
   Types & Defaults
   =========================================================================== */

export interface DepthTextOptions {
  depth?: string; // e.g. "1rem", "20px"
  direction?: "both" | "backwards" | "forwards";
  event?: "none" | "pointer" | "scroll" | "scrollX" | "scrollY";
  eventRotation?: string; // e.g. "30deg"
  eventDirection?: "default" | "reverse";
  fade?: boolean | string;
  layers?: number | string;
  perspective?: string;
  engaged?: boolean | string;
  addClass?: string;
  layerClassMap?: string[];
}

export const DEPTHTEXT_DEFAULTS: Required<Pick<DepthTextOptions, "depth" | "direction" | "event" | "eventRotation" | "eventDirection" | "fade" | "layers" | "perspective" | "engaged" | "addClass" | "layerClassMap">> = {
  depth: "1rem",
  direction: "both",
  event: "none",
  eventRotation: "30deg",
  eventDirection: "default",
  fade: false,
  layers: 10,
  perspective: "500px",
  engaged: true,
  addClass: "",
  layerClassMap: [],
};

/* ===========================================================================
   Global manager (singleton) - keeps one RAF and one pointer/scroll listener
   =========================================================================== */

type MaybeElement = HTMLElement | Element | null;

const DEPTHTEXT_GLOBAL = {
  instances: new Set<DepthTextInstance>(),
  rafId: null as number | null,

  // Target values (where we want to be)
  targetX: 0,
  targetY: 0,

  // Current values (where we are currently - smoothed)
  currentX: 0,
  currentY: 0,

  // Smoothing factor (0.1 = smooth, 1 = instant)
  lerpFactor: 0.1,

  // State
  isMoving: false,
  hasPointerListener: false,
  hasScrollListener: false,

  requestLoop() {
    if (this.isMoving) return;
    this.isMoving = true;
    this.tick();
  },

  tick() {
    // 1. Interpolate pointer values
    // Simple lerp: current = current + (target - current) * factor
    const dx = this.targetX - this.currentX;
    const dy = this.targetY - this.currentY;

    this.currentX += dx * this.lerpFactor;
    this.currentY += dy * this.lerpFactor;

    // Check if we are "close enough" to stop (optimization)
    const isSettled = Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001;

    // 2. Update all instances
    this.updateAll();

    // 3. Continue loop if not settled, otherwise stop
    if (!isSettled) {
      this.rafId = requestAnimationFrame(() => this.tick());
    } else {
      // Snap to target to finish
      this.currentX = this.targetX;
      this.currentY = this.targetY;
      this.updateAll(); // Final update
      this.isMoving = false;
      this.rafId = null;
    }
  },

  updateAll() {
    this.instances.forEach((inst) => {
      if (!inst.isVisible) return;

      // POINTER: Use the smoothed global values
      if (inst.options.event === "pointer") {
        inst.tilt(this.currentX, this.currentY);
      }

      // SCROLL: Direct update for scroll events (no lerp for scroll - immediate response)
      if (inst.options.event === "scroll" || inst.options.event === "scrollX" || inst.options.event === "scrollY") {
        inst.handleScrollGlobal();
      }
    });
  },

  attachPointerIfNeeded() {
    if (this.hasPointerListener) return;
    if (typeof window === "undefined") return;

    this._pointerHandler = (e: PointerEvent | TouchEvent) => {
      let cx = 0,
        cy = 0;
      if ("clientX" in e && typeof (e as PointerEvent).clientX === "number") {
        cx = (e as PointerEvent).clientX;
        cy = (e as PointerEvent).clientY;
      } else if ("touches" in e && (e as TouchEvent).touches && (e as TouchEvent).touches.length) {
        cx = (e as TouchEvent).touches[0].clientX;
        cy = (e as TouchEvent).touches[0].clientY;
      }
      // Update TARGET values
      this.targetX = (cx / window.innerWidth - 0.5) * 2;
      this.targetY = (cy / window.innerHeight - 0.5) * 2;

      // Start the loop
      this.requestLoop();
    };

    window.addEventListener("pointermove" as any, this._pointerHandler, { passive: true } as any);
    this.hasPointerListener = true;
  },

  detachPointerIfUnused() {
    const need = [...this.instances].some((i) => i.options.event === "pointer");
    if (!need && this.hasPointerListener && typeof window !== "undefined") {
      window.removeEventListener("pointermove" as any, this._pointerHandler as any);
      this._pointerHandler = undefined;
      this.hasPointerListener = false;
    }
  },

  attachScrollIfNeeded() {
    if (this.hasScrollListener) return;
    if (typeof window === "undefined") return;

    this._scrollHandler = () => {
      // Direct update for scroll to ensure responsiveness
      requestAnimationFrame(() => this.updateAll());
    };

    window.addEventListener("scroll", this._scrollHandler, { passive: true } as any);
    this.hasScrollListener = true;
  },

  detachScrollIfUnused() {
    const need = [...this.instances].some((i) => ["scroll", "scrollX", "scrollY"].includes(i.options.event));
    if (!need && this.hasScrollListener && typeof window !== "undefined") {
      window.removeEventListener("scroll", this._scrollHandler as any);
      this._scrollHandler = undefined;
      this.hasScrollListener = false;
    }
  },

  // internals (set after creation)
  _pointerHandler: undefined as undefined | ((e: any) => void),
  _scrollHandler: undefined as undefined | (() => void),
};

/* expose for debugging in browser, optional */
if (typeof window !== "undefined") {
  (window as any).DEPTHTEXT_GLOBAL = DEPTHTEXT_GLOBAL;
}

/* ===========================================================================
   DepthTextInstance - core class
   =========================================================================== */

export class DepthTextInstance {
  element: MaybeElement;
  options: Required<DepthTextOptions>;
  wrapper: MaybeElement | null = null;
  layersContainer: MaybeElement | null = null;
  _layerContentTemplate: DocumentFragment | null = null;
  _originalStyles = new Map<string, string>();
  intersectionObserver: any = null;
  resizeObserver: any = null;
  depthValue = 0;
  depthUnit = "rem";
  rotationValue = 0;
  rotationUnit = "deg";
  isVisible = true;
  _isDestroyed = false;

  constructor(element: MaybeElement, options: DepthTextOptions = {}) {
    if (!element) throw new Error("DepthTextInstance: element required");
    this.element = element;
    this.options = this._validateOptions(options);
    // parse and init only if engaged
    if (this.options.engaged === false) return;
    this._parseUnits();
    this._storeOriginalState();
    this._createStructure();
    this._setupObservers();
    this._enableGlobalHooks();
  }

  /* -------------------------
     options validation
  ------------------------- */
  _validateOptions(options: DepthTextOptions): Required<DepthTextOptions> {
    const normalizeAddClass = (v: any): string => {
      if (!v && v !== "") return DEPTHTEXT_DEFAULTS.addClass;
      if (Array.isArray(v)) return v.map(String).join(" ").trim().replace(/\s+/g, " ");
      return String(v).trim().replace(/\s+/g, " ");
    };

    // FIX: engaged validation correcte
    const normalizeEngaged = (v: any): boolean => {
      if (v === false || v === "false") return false;
      if (v === true || v === "true") return true;
      if (v === undefined || v === null) return DEPTHTEXT_DEFAULTS.engaged as boolean;
      return Boolean(v);
    };

    // FIX: event validation
    const normalizeEvent = (v: any): "none" | "pointer" | "scroll" | "scrollX" | "scrollY" => {
      const validEvents = ["none", "pointer", "scroll", "scrollX", "scrollY"];
      if (v && validEvents.includes(String(v))) {
        return v as any;
      }
      if (v && !validEvents.includes(String(v))) {
        console.warn(`DepthText: Invalid event "${v}", defaulting to "none"`);
      }
      return DEPTHTEXT_DEFAULTS.event as any;
    };

    const merged: Required<DepthTextOptions> = {
      depth: options.depth ?? DEPTHTEXT_DEFAULTS.depth,
      direction: options.direction ?? DEPTHTEXT_DEFAULTS.direction,
      event: normalizeEvent(options.event),
      eventRotation: options.eventRotation ?? DEPTHTEXT_DEFAULTS.eventRotation,
      eventDirection: options.eventDirection ?? DEPTHTEXT_DEFAULTS.eventDirection,
      perspective: options.perspective ?? DEPTHTEXT_DEFAULTS.perspective,
      fade: options.fade === true || options.fade === "true" ? true : DEPTHTEXT_DEFAULTS.fade,
      layers: ((): number => {
        const raw = options.layers ?? DEPTHTEXT_DEFAULTS.layers;
        const n = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
        const clamped: number = isFinite(n) ? Math.min(Math.max(1, n), 40) : Number(DEPTHTEXT_DEFAULTS.layers);
        if (clamped > 25) console.warn(`DepthText: ${clamped} layers may affect performance`);
        return clamped;
      })(),
      engaged: normalizeEngaged(options.engaged),
      addClass: normalizeAddClass(options.addClass ?? DEPTHTEXT_DEFAULTS.addClass),
      layerClassMap: Array.isArray(options.layerClassMap) ? options.layerClassMap.map(String) : DEPTHTEXT_DEFAULTS.layerClassMap,
    };
    return merged;
  }

  /* -------------------------
     parse numeric units
  ------------------------- */
  _parseUnits() {
    const d = String(this.options.depth);
    const dNum = parseFloat(d);
    this.depthValue = isFinite(dNum) ? dNum : 1;
    this.depthUnit = d.replace(/[0-9.\-\s]/g, "") || "rem";

    const r = String(this.options.eventRotation);
    const rNum = parseFloat(r);
    this.rotationValue = isFinite(rNum) ? rNum : 30;
    this.rotationUnit = r.replace(/[0-9.\-\s]/g, "") || "deg";
  }

  /* -------------------------
     store original content and styles (create a single template clone)
  ------------------------- */
  _storeOriginalState() {
    const styleProps = ["display", "position", "perspective", "webkitPerspective"];
    styleProps.forEach((p) => {
      try {
        const elStyle = (this.element as HTMLElement | null)?.style;
        if (elStyle) {
          // CSSStyleDeclaration doesn't have an index signature, so cast to any for dynamic property access
          this._originalStyles.set(p, (elStyle as any)[p] ?? "");
        } else {
          this._originalStyles.set(p, "");
        }
      } catch {
        this._originalStyles.set(p, "");
      }
    });

    // Pull children into a fragment
    const temp = ((): DocumentFragment => {
      const frag = document.createDocumentFragment();
      try {
        while (this.element && (this.element as any).firstChild) {
          frag.appendChild((this.element as any).firstChild);
        }
      } catch {
        // SSR-safety: if element isn't DOM-like, do nothing
      }
      return frag;
    })();

    // store a clone to use as template for each layer
    this._layerContentTemplate = temp.cloneNode(true) as DocumentFragment;
  }

  /* -------------------------
     create DOM structure
  ------------------------- */
  _createStructure() {
    // Apply basic inline styles to host element (safe: doesn't require window)
    try {
      const elStyle = (this.element as any).style;
      if (elStyle) {
        elStyle.display = elStyle.display || "inline-block";
        elStyle.position = elStyle.position || "relative";
        elStyle.perspective = elStyle.perspective || this.options.perspective;
        elStyle.webkitPerspective = elStyle.webkitPerspective || this.options.perspective;
      }
    } catch {
      /* ignore for non-DOM hosts */
    }

    // wrapper that will receive will-change & transform
    const wrapper = document.createElement ? document.createElement("span") : ({} as any);
    wrapper.className = "depthtext-wrapper";
    try {
      Object.assign(wrapper.style, {
        display: "inline-block",
        transformStyle: "preserve-3d",
        willChange: "transform",
      });
    } catch {
      /* ignore */
    }
    this.wrapper = wrapper;

    // layers container
    const layersContainer = document.createElement ? document.createElement("span") : ({} as any);
    layersContainer.className = "depthtext-layers";
    try {
      Object.assign(layersContainer.style, {
        display: "inline-block",
        transformStyle: "preserve-3d",
      });
      layersContainer.setAttribute("aria-hidden", "true");
    } catch {
      /* ignore */
    }
    this.layersContainer = layersContainer;

    // append
    try {
      (wrapper as any).appendChild(layersContainer);
      (this.element as any).appendChild(wrapper);
    } catch {
      // if element isn't DOM-like, skip append (still usable programmatically)
    }

    // build layers (off DOM)
    this._createLayers();
  }

  _createLayers() {
    const frag = document.createDocumentFragment();
    const layers = Number(this.options.layers);
    const dir = this.options.direction;
    const fade = Boolean(this.options.fade);
    const template = this._layerContentTemplate;

    for (let i = 0; i < layers; i++) {
      const layer = document.createElement ? document.createElement("span") : ({} as any);

      // FIX: Utiliser un Set pour éviter les doublons de classes
      const classSet = new Set<string>();

      /* Base class */
      classSet.add("depthtext-layer");

      /* Layer index class (0-indexed) */
      classSet.add(`depthtext-layer-${i}`);

      /* layerClassMap[i] - Optional per-layer custom classes */
      if (this.options.layerClassMap && this.options.layerClassMap[i]) {
        const mapped = String(this.options.layerClassMap[i])
          .split(/\s+/)
          .map((c) => c.trim())
          .filter(Boolean);
        mapped.forEach((cls) => classSet.add(cls));
      }

      /* addClass - Applied to ALL layers */
      if (this.options.addClass) {
        const extra = String(this.options.addClass)
          .split(/\s+/)
          .map((c) => c.trim())
          .filter(Boolean);
        extra.forEach((cls) => classSet.add(cls));
      }

      // Appliquer toutes les classes uniques
      classSet.forEach((cls) => {
        try {
          layer.classList.add(cls);
        } catch {}
      });

      /* Clone content per layer */
      if (template && layer.appendChild) {
        layer.appendChild(template.cloneNode(true));
      }

      /* Positioning / transforms */
      const pct = i / layers;
      const t = this._calculateTranslation(pct, dir);

      try {
        layer.style.transform = `translateZ(${t}${this.depthUnit})`;
        layer.style.display = "inline-block";
      } catch {}

      if (i >= 1) {
        try {
          Object.assign(layer.style, {
            position: "absolute",
            top: "0",
            left: "0",
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          });
          layer.setAttribute("aria-hidden", "true");

          if (fade) {
            layer.style.opacity = String((1 - pct) / 2);
          }
        } catch {}
      }

      frag.appendChild(layer);
    }

    try {
      this.layersContainer?.appendChild(frag);
    } catch {}
  }

  _calculateTranslation(pct: number, direction: string) {
    switch (direction) {
      case "backwards":
        return -pct * this.depthValue;
      case "forwards":
        return -(pct * this.depthValue) + this.depthValue;
      case "both":
      default:
        return -(pct * this.depthValue) + this.depthValue / 2;
    }
  }

  /* -------------------------
     IntersectionObserver + ResizeObserver
  ------------------------- */
  _setupObservers() {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      // noop for SSR or very old envs
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (!entry) return;
        this.isVisible = entry.isIntersecting;
        if (!this.isVisible) this.tilt(0, 0);
        else DEPTHTEXT_GLOBAL.updateAll();
      },
      { threshold: 0 }
    );

    try {
      this.intersectionObserver.observe(this.element as Element);
    } catch {
      /* ignore */
    }

    if (typeof ResizeObserver !== "undefined") {
      try {
        // FIX: Protéger le callback avec check de destruction
        this.resizeObserver = new ResizeObserver(() => {
          if (!this._isDestroyed && this.isVisible && DEPTHTEXT_GLOBAL.instances.has(this)) {
            DEPTHTEXT_GLOBAL.updateAll();
          }
        });
        this.resizeObserver.observe(this.element as Element);
      } catch {
        /* ignore */
      }
    }
  }

  /* -------------------------
     Register with global manager & attach global listeners if needed
  ------------------------- */
  _enableGlobalHooks() {
    DEPTHTEXT_GLOBAL.instances.add(this);
    if (this.options.event === "pointer") DEPTHTEXT_GLOBAL.attachPointerIfNeeded();
    if (["scroll", "scrollX", "scrollY"].includes(this.options.event)) DEPTHTEXT_GLOBAL.attachScrollIfNeeded();
  }

  /* -------------------------
     Apply tilt (called by global RAF or pointer handler)
  ------------------------- */
  tilt(xPct = 0, yPct = 0) {
    if (!this.wrapper) return;
    const mul = this.options.eventDirection === "reverse" ? -1 : 1;
    const xTilt = xPct * this.rotationValue * mul;
    const yTilt = -yPct * this.rotationValue * mul;
    try {
      (this.wrapper as any).style.transform = `rotateX(${yTilt}${this.rotationUnit}) rotateY(${xTilt}${this.rotationUnit})`;
    } catch {
      /* ignore for non-DOM targets */
    }
  }

  /* -------------------------
     Recompute tilt from scroll (per-instance logic)
  ------------------------- */
  handleScrollGlobal() {
    if (typeof window === "undefined") return;
    try {
      const rect = (this.element as Element).getBoundingClientRect();
      let xPct = 0,
        yPct = 0;
      if (this.options.event === "scroll" || this.options.event === "scrollX") {
        const centerX = rect.left + rect.width / 2 - window.innerWidth / 2;
        xPct = (centerX / window.innerWidth) * 2;
      }
      if (this.options.event === "scroll" || this.options.event === "scrollY") {
        const centerY = rect.top + rect.height / 2 - window.innerHeight / 2;
        yPct = (centerY / window.innerHeight) * 2;
      }
      this.tilt(xPct, yPct);
    } catch {
      /* ignore in non-DOM contexts */
    }
  }

  /* -------------------------
     Public API: Update options dynamically
  ------------------------- */
  update(options?: Partial<DepthTextOptions>) {
    if (this._isDestroyed) {
      console.warn("DepthText: Cannot update destroyed instance");
      return;
    }
    if (options) {
      this.options = this._validateOptions({ ...this.options, ...options });
      this._parseUnits();
    }
    DEPTHTEXT_GLOBAL.updateAll();
  }

  /* -------------------------
     Destroy instance (cleanup)
  ------------------------- */
  destroy() {
    if (this._isDestroyed) return;
    this._isDestroyed = true;

    try {
      this.intersectionObserver?.disconnect();
    } catch {}
    try {
      this.resizeObserver?.disconnect();
    } catch {}

    // restore original content if template exists
    try {
      if (this.element) {
        (this.element as any).innerHTML = "";
        if (this._layerContentTemplate) {
          (this.element as any).appendChild(this._layerContentTemplate.cloneNode(true));
        }
      }
    } catch {
      /* ignore */
    }

    // restore inline styles
    this._originalStyles.forEach((v, k) => {
      try {
        (this.element as any).style[k] = v || "";
      } catch {}
    });

    DEPTHTEXT_GLOBAL.instances.delete(this);
    DEPTHTEXT_GLOBAL.detachPointerIfUnused();
    DEPTHTEXT_GLOBAL.detachScrollIfUnused();

    try {
      delete (this.element as any)._depthTextInstance;
    } catch {
      (this.element as any)._depthTextInstance = undefined;
    }
  }
}

/* ===========================================================================
   Auto-init + API
   - Auto-initialize elements with data-depth attributes
   - Provide global DepthTextify(selector, options)
   =========================================================================== */

export function DepthTextify(selectorOrOptions?: string | DepthTextOptions, maybeOptions?: DepthTextOptions) {
  // Overloads:
  // DepthTextify() -> init all [data-depth]
  // DepthTextify(selector) -> init selector
  // DepthTextify(selector, options) -> init selector with options
  // DepthTextify(undefined, options) -> init [data-depth] with options
  if (typeof window === "undefined") return [];

  let selector = "[data-depth]";
  let options: DepthTextOptions | undefined = undefined;

  if (typeof selectorOrOptions === "string") {
    selector = selectorOrOptions;
    options = maybeOptions;
  } else if (typeof selectorOrOptions === "object" && selectorOrOptions !== null) {
    // user passed options as first arg
    options = selectorOrOptions as DepthTextOptions;
  }

  const nodes = Array.from(document.querySelectorAll(selector));
  const instances: DepthTextInstance[] = [];
  nodes.forEach((el) => {
    try {
      // Skip if already initialized
      if ((el as any)._depthTextInstance) {
        return;
      }

      // FIX: Lecture correcte des data-attributes
      const dataset = (el as HTMLElement).dataset;

      // Helper pour normaliser les valeurs vides
      const getDataAttr = (key: string): string | undefined => {
        const val = dataset[key];
        return val === "" ? undefined : val;
      };

      const dataOptions: DepthTextOptions = {
        // depth peut être data-depth ou data-depth-depth
        depth: getDataAttr("depthDepth") ?? getDataAttr("depth"),

        direction: getDataAttr("depthDirection") as any,
        event: getDataAttr("depthEvent") as any,

        // Support à la fois camelCase et lowercase pour eventRotation
        eventRotation: getDataAttr("depthEventRotation") ?? getDataAttr("depthEventrotation"),
        eventDirection: getDataAttr("depthEventDirection") ?? (getDataAttr("depthEventdirection") as any),

        fade: getDataAttr("depthFade") as any,
        layers: getDataAttr("depthLayers") as any,
        perspective: getDataAttr("depthPerspective") as any,

        // FIX CRITIQUE: engaged utilise data-depth-engaged, PAS data-depth
        engaged: getDataAttr("depthEngaged") as any,

        // Support multiples variantes pour addClass
        addClass: getDataAttr("depthAddClass") ?? getDataAttr("depthAddclass") ?? getDataAttr("depthAdd") ?? getDataAttr("addClass"),
      };

      const mergedOptions = { ...(options || {}), ...(dataOptions || {}) };
      const inst = new DepthTextInstance(el as MaybeElement, mergedOptions);
      (el as any)._depthTextInstance = inst;
      instances.push(inst);
    } catch (err) {
      // swallow errors per-element so a broken node doesn't stop others
      console.error("DepthTextify: failed to initialize element", err);
    }
  });

  return instances.length === 1 ? instances[0] : instances;
}

/* Auto init on DOMContentLoaded */
if (typeof window !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    DepthTextify();
  });

  // Expose on window for convenience
  try {
    (window as any).DepthTextify = DepthTextify;
  } catch {
    /* ignore */
  }
}

/* Export default names */
export default DepthTextInstance;
